// Rogers' screen 03 chart, two panels on the same year axis (history.js, drawn top to bottom):
//   * input cost per acre — one dumbbell per year, September's expected cost against April's
//     settled cost, the rule between them colored by which way it moved;
//   * what each product would have paid this operation — MCO against ECO, paired bars.
// The cost panel is what the payment panel is explained BY, which is why it sits above it and
// carries the same clickable years. Vega-Lite via vega-embed, matching the house pattern
// (DebtServiceStackedBarChart.jsx). Clicking anything in either panel fires onSelectYear so the
// panel can drive its year-detail card.
//
// Two sibling charts rather than one vconcat: Vega-Lite's responsive `width: "container"` is
// supported only for single and layered views, never for concatenated ones, and this lives in a
// resizable dialog. The two panels therefore align by sharing the year domain, not by layout.

import { useEffect, useMemo, useRef } from "react";

import { useTheme } from "@emotion/react";
import { Box, Stack, Typography } from "@mui/material";
import vegaEmbed from "vega-embed";

// Blue is ECO's bar, green is MCO's, per the reference app's legend (history.js). The hues are
// the concept preview's backtest page rather than history.js's #3f8462/#4a7ba7 — sampled off that
// chart, which runs these bars brighter and more saturated than the tracker's lines.
export const COLOR_ECO = "#2f6faf";
export const COLOR_MCO = "#4e801f";

// The cost panel's own pair, from the reference legend: blue is the cost September expected,
// orange the cost April settled at. The rule between them takes the direction's color — red for
// a cost that rose (the move that eats the margin), green for one that fell.
const COLOR_COST_EXPECTED = "#4a7ba7";
const COLOR_COST_SETTLED = "#a9632f";
const COLOR_ROSE = "#a84c3f";
const COLOR_FELL = "#3f8462";

// Vega sizes a point by AREA, so this is the reference's r = 4.5 dot (history.js DOT) as it
// actually renders: its 620-unit viewBox stretched to this panel's ~1110px is a 1.8x scale, so
// r = 8px, and pi * 8^2 ~ 200.
const DOT_SIZE = 200;

// Bar geometry is the reference's, converted from its fixed pixels to the band-scale ratios that
// hold at any container width (history.js: span = 542 / years, BW = 17, the pair drawn at
// x - BW - 2 and x + 2).
//   * The pair occupies 38 of each year's 108px span, so bandwidth / step = 0.35 and the year
//     scale's paddingInner is 0.65. Its paddingOuter is half that, which is what makes the step
//     exactly width / years — the reference centers each year at span / 2 + i * span, with no
//     extra margin at the ends.
//   * The two bars sit 4px apart within those 38, so the pair's paddingInner is 4 / 38.
// Deliberately not a touching pair: a hairline between them is the reference's, and it keeps a
// blowout year (both products capped, identical heights) from reading as one wide block.
const YEAR_SCALE = { paddingInner: 0.65, paddingOuter: 0.325 };
const PAIR_SCALE = { paddingInner: 4 / 38 };

// One row per (year, product) actually plotted, plus annotation-only rows for states that
// must never render as a $0 bar: a whole year MCO hasn't resolved (product: null, spans the
// year), or an ECO offer that doesn't exist/isn't ready (product: "ECO", value: 0, labeled).
function buildPaymentRows(years) {
  const rows = [];
  for (const y of years) {
    if (!y.ready) {
      rows.push({ year: y.year, product: null, value: null, label: "not yet determined" });
      continue;
    }
    rows.push({ year: y.year, product: "MCO", value: y.indemnityPerAcre, label: null });
    const eco = y.eco || {};
    if (!eco.applicable) continue; // pre-2021: ECO didn't exist yet, leave blank
    if (eco.offered === false) {
      rows.push({ year: y.year, product: "ECO", value: 0, label: eco.note || "no comparable ECO offer" });
    } else if (!eco.ready) {
      rows.push({ year: y.year, product: "ECO", value: 0, label: "not yet determined" });
    } else {
      rows.push({ year: y.year, product: "ECO", value: eco.result.indemnityPerAcre, label: null });
    }
  }
  return rows;
}

// One dumbbell per year that has an expected cost. A year still waiting on its April settlement
// keeps its expected dot and simply has no partner — `settled: null` filters the rule, the second
// dot and the delta label out, which is the honest rendering of "not settled yet".
function buildCostRows(years) {
  return years
    .filter((y) => y.expectedCost != null)
    .map((y) => {
      const settled = y.harvestCost ?? null;
      const delta = settled == null ? null : settled - y.expectedCost;
      return {
        year: y.year,
        expected: y.expectedCost,
        settled,
        delta,
        // The label sits above whichever dot is higher, so it never lands on the rule.
        top: settled == null ? y.expectedCost : Math.max(y.expectedCost, settled),
        direction: delta == null ? null : delta > 0 ? "rose" : "fell",
        deltaLabel: delta == null ? null : `${delta > 0 ? "+" : "\u2212"}$${Math.abs(delta).toFixed(0)}`,
      };
    });
}

/** Embed one spec and route every click on it to onSelectYear. Returns the container ref. */
function useYearChart(spec, onSelectYear, errorLabel) {
  const chartRef = useRef(null);
  const viewRef = useRef(null);
  const onSelectYearRef = useRef(onSelectYear);
  onSelectYearRef.current = onSelectYear;

  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.finalize();
      viewRef.current = null;
    }
    if (!chartRef.current || !spec) return undefined;

    vegaEmbed(chartRef.current, spec, { actions: false, renderer: "svg" })
      .then((result) => {
        viewRef.current = result.view;
        result.view.addEventListener("click", (_event, item) => {
          const raw = item?.datum?.year ?? item?.datum;
          const year = typeof raw === "number" ? raw : Number(raw);
          if (Number.isFinite(year) && onSelectYearRef.current) onSelectYearRef.current(year);
        });
      })
      .catch((error) => console.error(`Error rendering ${errorLabel}:`, error));

    return () => {
      if (viewRef.current) {
        viewRef.current.finalize();
        viewRef.current = null;
      }
    };
  }, [spec, errorLabel]);

  return chartRef;
}

export default function McoBacktestChart({ years, selectedYear, onSelectYear }) {
  const theme = useTheme();

  const yearList = years || [];
  const paymentRows = useMemo(() => buildPaymentRows(yearList), [yearList]);
  const costRows = useMemo(() => buildCostRows(yearList), [yearList]);

  // Pinned so both panels place a year at the same band even though the cost panel drops years
  // that have no expected cost. Without it the two axes would silently disagree.
  const yearDomain = useMemo(() => yearList.map((y) => y.year), [yearList]);

  const chartConfig = useMemo(
    () => ({
      background: "transparent",
      view: { stroke: null },
      axis: {
        labelColor: theme.palette.text.primary,
        titleColor: theme.palette.text.primary,
        labelFontSize: 12,
        titleFontSize: 13,
      },
    }),
    [theme]
  );

  const costSpec = useMemo(() => {
    if (!costRows.length) return null;
    // Shared across the layers, so all three y encodings must agree: costs sit in a narrow band
    // well above zero, and a zero-based axis would flatten every dumbbell into the same dot.
    // The pixel padding is what keeps the delta label (drawn above the higher dot) inside the
    // panel in the year whose cost peaked.
    const yEnc = (field) => ({
      field,
      type: "quantitative",
      title: "$/acre",
      scale: { zero: false, nice: true, padding: 20 },
    });
    const directionColor = {
      field: "direction",
      type: "nominal",
      scale: { domain: ["rose", "fell"], range: [COLOR_ROSE, COLOR_FELL] },
      legend: null,
    };
    const settled = [{ filter: "datum.settled != null" }];

    return {
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      config: chartConfig,
      title: {
        text: "Input cost per acre",
        subtitle: "what September expected, and what April settled",
        color: theme.palette.text.primary,
        subtitleColor: theme.palette.text.secondary,
        fontSize: 13,
        subtitleFontSize: 11,
        anchor: "start",
      },
      data: { values: costRows },
      width: "container",
      height: 170,
      encoding: {
        // The years read off the payment panel below; repeating them here would only push the
        // two plot areas further out of step.
        x: {
          field: "year",
          type: "ordinal",
          title: null,
          scale: { ...YEAR_SCALE, domain: yearDomain },
          axis: { labels: false, ticks: false, domain: false },
        },
        tooltip: [
          { field: "year", title: "Year" },
          { field: "expected", title: "Expected, Sep 14", format: "$,.2f" },
          { field: "settled", title: "Settled, Apr 30", format: "$,.2f" },
          { field: "delta", title: "Move", format: "+$,.2f" },
        ],
      },
      layer: [
        {
          mark: { type: "rule", strokeWidth: 3, cursor: "pointer" },
          transform: settled,
          encoding: { y: yEnc("expected"), y2: { field: "settled" }, color: directionColor },
        },
        {
          mark: { type: "point", filled: true, size: DOT_SIZE, color: COLOR_COST_EXPECTED, cursor: "pointer" },
          encoding: { y: yEnc("expected") },
        },
        {
          mark: { type: "point", filled: true, size: DOT_SIZE, color: COLOR_COST_SETTLED, cursor: "pointer" },
          transform: settled,
          encoding: { y: yEnc("settled") },
        },
        {
          mark: { type: "text", dy: -18, fontSize: 12, fontWeight: 600, cursor: "pointer" },
          transform: settled,
          encoding: { y: yEnc("top"), text: { field: "deltaLabel" }, color: directionColor },
        },
      ],
    };
  }, [costRows, yearDomain, chartConfig, theme]);

  const vegaSpec = useMemo(() => {
    if (!paymentRows.length) return null;
    const textColor = theme.palette.text.primary;
    const mutedColor = theme.palette.text.secondary;

    return {
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      config: chartConfig,
      title: {
        text: "What each product would have paid this operation",
        color: textColor,
        fontSize: 13,
        anchor: "start",
      },
      data: { values: paymentRows },
      width: "container",
      height: 220,
      encoding: {
        x: { field: "year", type: "ordinal", title: "Crop year", scale: { ...YEAR_SCALE, domain: yearDomain } },
      },
      layer: [
        {
          mark: { type: "bar", cursor: "pointer" },
          transform: [{ filter: "datum.product != null" }],
          encoding: {
            xOffset: { field: "product", sort: ["MCO", "ECO"], scale: PAIR_SCALE },
            y: { field: "value", type: "quantitative", title: "$/acre" },
            color: {
              field: "product",
              type: "nominal",
              scale: { domain: ["MCO", "ECO"], range: [COLOR_MCO, COLOR_ECO] },
              legend: null,
            },
            tooltip: [
              { field: "year", title: "Year" },
              { field: "product", title: "Product" },
              { field: "value", title: "$/acre", format: "$,.2f" },
            ],
          },
        },
        {
          mark: { type: "text", dy: 14, fontSize: 10, fontStyle: "italic", color: mutedColor },
          transform: [{ filter: "datum.label != null && datum.product != null" }],
          encoding: {
            xOffset: { field: "product", sort: ["MCO", "ECO"], scale: PAIR_SCALE },
            y: { value: 0 },
            text: { field: "label" },
          },
        },
        {
          mark: { type: "text", dy: 14, fontSize: 11, fontWeight: 600, color: mutedColor, cursor: "pointer" },
          transform: [{ filter: "datum.label != null && datum.product == null" }],
          encoding: {
            y: { value: 0 },
            text: { field: "label" },
          },
        },
        // Underlines the year the detail card below is describing. Drawn at the baseline in
        // pixels rather than in data units so an all-zero payment year still marks.
        ...(selectedYear == null
          ? []
          : [
              {
                data: { values: [{ year: selectedYear }] },
                mark: { type: "tick", orient: "horizontal", thickness: 2, size: 56, color: textColor },
                encoding: { y: { value: 220 } },
              },
            ]),
      ],
    };
  }, [paymentRows, yearDomain, selectedYear, chartConfig, theme]);

  const costChartRef = useYearChart(costSpec, onSelectYear, "MCO backtest cost panel");
  const chartRef = useYearChart(vegaSpec, onSelectYear, "MCO backtest chart");

  if (!paymentRows.length) {
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No backtest data available for this county, crop and vintage.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      {costSpec && <div ref={costChartRef} style={{ width: "100%" }} />}
      <div ref={chartRef} style={{ width: "100%" }} />
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5, mb: 1 }}>
        {[
          [COLOR_COST_EXPECTED, "cost expected, set Sep 14"],
          [COLOR_COST_SETTLED, "cost settled, Apr 30"],
          [COLOR_MCO, "MCO would have paid"],
          [COLOR_ECO, "ECO would have paid"],
        ].map(([color, label]) => (
          <Stack key={label} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
